/** @jsxImportSource @emotion/react */

import { useMemo, useCallback } from 'react'
import { ApolloQueryResult, useLazyQuery } from '@apollo/client'
import {
  findSchoolListByPlatform,
  findSchoolListByPlatformVariables,
} from '__generatedTypes__/findSchoolListByPlatform'
import { PlatformName } from '__generatedTypes__/globalTypes'
import { FIND_SCHOOL_LIST_BY_PLATFORM } from 'queries/faculty'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { findOneUser, findOneUserVariables } from '__generatedTypes__/findOneUser'

export type RefetchUser = (
  variables?: Partial<findOneUserVariables> | undefined
) => Promise<ApolloQueryResult<findOneUser>>

export type School = {
  id: string
  title: string
}

export const NOT_SELECTED_SCHOOL: School = {
  id: 'none',
  title: 'None',
}

export const useFacultySchools = () => {
  const { platforms } = usePlatformsAndSubjects({})

  const [getSchools, { data, loading }] = useLazyQuery<findSchoolListByPlatform, findSchoolListByPlatformVariables>(
    FIND_SCHOOL_LIST_BY_PLATFORM,
    { fetchPolicy: 'cache-and-network' }
  )

  const schools = useMemo(
    () =>
      data?.findSchoolListByPlatform?.schoolArray?.map(
        (school) =>
          ({
            id: school.key,
            title: school.value,
          } as School)
      ),
    [data?.findSchoolListByPlatform]
  )

  const fetchSchools = useCallback(
    async (platformName: PlatformName) => {
      const selectedPlatform = platforms.find((platform) => platform.title === platformName)

      if (selectedPlatform) {
        return getSchools({
          variables: {
            platformId: selectedPlatform.id,
          },
        })
      }
    },
    [platforms, getSchools]
  )

  return {
    loading,
    schools,
    fetchSchools,
  }
}
